import { ReactNode, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  trigger: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Generic collapsible section — collapsed by default, click the trigger row
 * to expand. No existing accordion/collapsible primitive was found anywhere
 * in this codebase (confirmed by survey), so this is genuinely new, but kept
 * as a small, reusable, content-agnostic component rather than a one-off
 * built into the API docs page — plain React state + Tailwind, matching
 * this project's existing style (no new dependency needed for something
 * this simple).
 */
export function Collapsible({ trigger, children, defaultOpen = false, className }: Props) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={cn('rounded-lg border border-slate-200 bg-white', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0 flex-1">{trigger}</div>
        <ChevronDown
          className={cn('h-4 w-4 flex-shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="border-t border-slate-200 p-4">{children}</div>}
    </div>
  );
}
